import { IndicationItem } from 'features/indicationTriage/api/indicationTriageApi'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import React, { createContext, FC, ReactNode, useMemo, useState } from 'react'
import {
  GeckoComponentDataFrame,
  GeckoComponentModel,
  GeckoInspection,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

import {
  NormalizedFfsDataValues,
  useFfsStrategy,
} from './ffsStrategyContext/FfsStrategyContext'
import {
  DEFAULT_STRESS_VALUE,
  DEFAULT_TENSILTE_STRENGTH,
} from './mawpSection/allowableStress/MetalAllowableStressTable'
import { useFfsPartFourDataValues } from './part4/FfsDataValuesSection'
import { useFfsPartFiveDataValues } from './part5LevelOne/FfsDataValuesSection'
import { ValueSetterPair } from './typeUtils'

type MeasurementType = GeckoMeasurement | null

export type FfsSharedValuesType = ValueSetterPair<MeasurementType, 'fca'> &
  ValueSetterPair<MeasurementType, 'fcaMl'> &
  ValueSetterPair<MeasurementType, 'corrosionRate'> &
  ValueSetterPair<MeasurementType, 'corrosionRateMl'> &
  ValueSetterPair<string | null, 'targetDate'> &
  ValueSetterPair<string | null, 'inspectionDate'> &
  ValueSetterPair<MeasurementType, 'jointEfficiency'> &
  ValueSetterPair<MeasurementType, 'yieldStrength'> &
  ValueSetterPair<MeasurementType, 'tensileStrength'> &
  ValueSetterPair<MeasurementType, 'designTemp'> &
  ValueSetterPair<MeasurementType, 'designPressure'> &
  ValueSetterPair<MawpCalcThicknessOption, 'selectedMawpCalcThickness'> &
  ValueSetterPair<MawpCalcType | null, 'calcType'> & {
    // Read-only computed/derived values
    indicationItem: IndicationItem | undefined
    ffsDataValues: NormalizedFfsDataValues | null
  }

function formatInspectionDate(
  inspection: GeckoInspection | undefined
): string | null {
  if (!inspection?.data.date) return null
  const date = new Date(inspection.data.date)
  return date.toISOString().split('T')[0]
}

export type MawpCalcThicknessOption = 't_mm' | 't_am' | 't_c' | null
export type MawpCalcType = 'thinWall' | 'thickWall'

function useCreateFfsSharedValues(
  componentModel: GeckoComponentModel | undefined,
  thicknessCdf: GeckoComponentDataFrame | undefined,
  inspection: GeckoInspection | undefined
): FfsSharedValuesType {
  const { strategy } = useFfsStrategy()
  const [fca, setFca] = useState<MeasurementType>(
    GeckoMeasurement.CreateFromValue(0, GeckoMeasurementUnit.Millimeters)
  )
  const [fcaMl, setFcaMl] = useState<MeasurementType>(
    GeckoMeasurement.CreateFromValue(0, GeckoMeasurementUnit.Millimeters)
  )
  const [corrosionRate, setCorrosionRate] = useState<MeasurementType>(
    GeckoMeasurement.CreateFromValue(0, GeckoMeasurementUnit.MillimetersPerYear)
  )
  const [corrosionRateMl, setCorrosionRateMl] = useState<MeasurementType>(
    GeckoMeasurement.CreateFromValue(0, GeckoMeasurementUnit.MillimetersPerYear)
  )
  const [targetDate, setTargetDate] = useState<string | null>(null)
  const [inspectionDate, setInspectionDate] = useState<string | null>(
    formatInspectionDate(inspection)
  )
  const [jointEfficiency, setJointEfficiency] = useState<MeasurementType>(
    GeckoMeasurement.CreateFromValue(1, GeckoMeasurementUnit.Ratio)
  )
  const [yieldStrength, setYieldStrength] =
    useState<MeasurementType>(DEFAULT_STRESS_VALUE)
  const [tensileStrength, setTensileStrength] = useState<MeasurementType>(
    DEFAULT_TENSILTE_STRENGTH
  )
  const [designTemp, setDesignTemp] = useState<MeasurementType>(
    GeckoMeasurement.CreateFromValue(30, GeckoMeasurementUnit.Celsius)
  )
  // TODO @choikh: pull design pressure / temp from asset model
  const [designPressure, setDesignPressure] = useState<MeasurementType>(() => {
    if (componentModel && 'designPressure' in componentModel) {
      const pipeSection = componentModel
      return (
        pipeSection['designPressure'] ||
        GeckoMeasurement.CreateFromValue(
          150,
          GeckoMeasurementUnit.PoundsForcePerSqInch
        )
      )
    }
    return GeckoMeasurement.CreateFromValue(
      150,
      GeckoMeasurementUnit.PoundsForcePerSqInch
    )
  })
  const [selectedMawpCalcThickness, setSelectedMawpCalcThickness] =
    useState<MawpCalcThicknessOption>(null)
  const [calcType, setCalcType] = useState<MawpCalcType | null>(null)

  // Get indication data
  const { indication: indicationItem } = useIndicationData()

  // Calculate FFS data values using passed props (not state)
  const rawPartFourDataValues = useFfsPartFourDataValues({
    indicationItem,
    fca,
    fcaMl,
    componentModel,
    thicknessCdf,
  })

  const rawPartFiveDataValues = useFfsPartFiveDataValues({
    indicationItem,
    fca,
    componentModel,
    thicknessCdf,
  })

  // Normalize data values to consistent structure
  const ffsDataValues: NormalizedFfsDataValues | null = useMemo(
    () =>
      strategy.normalizeDataValues({
        partFour: rawPartFourDataValues,
        partFive: rawPartFiveDataValues,
      }),
    [strategy, rawPartFourDataValues, rawPartFiveDataValues]
  )

  return useMemo(
    () => ({
      fca,
      setFca,
      fcaMl,
      setFcaMl,
      corrosionRate,
      setCorrosionRate,
      corrosionRateMl,
      setCorrosionRateMl,
      targetDate,
      setTargetDate,
      inspectionDate,
      setInspectionDate,
      jointEfficiency,
      setJointEfficiency,
      yieldStrength,
      setYieldStrength,
      tensileStrength,
      setTensileStrength,
      designTemp,
      setDesignTemp,
      designPressure,
      setDesignPressure,
      selectedMawpCalcThickness,
      setSelectedMawpCalcThickness,
      calcType,
      setCalcType,
      indicationItem,
      ffsDataValues,
    }),
    [
      fca,
      fcaMl,
      corrosionRate,
      corrosionRateMl,
      targetDate,
      inspectionDate,
      jointEfficiency,
      tensileStrength,
      yieldStrength,
      designTemp,
      designPressure,
      selectedMawpCalcThickness,
      calcType,
      indicationItem,
      ffsDataValues,
    ]
  )
}

export const FfsSharedValuesContext = createContext<FfsSharedValuesType>({
  fca: null,
  setFca: () => {},
  fcaMl: null,
  setFcaMl: () => {},
  corrosionRate: null,
  setCorrosionRate: () => {},
  corrosionRateMl: null,
  setCorrosionRateMl: () => {},
  targetDate: null,
  setTargetDate: () => {},
  inspectionDate: null,
  setInspectionDate: () => {},
  jointEfficiency: null,
  setJointEfficiency: () => {},
  yieldStrength: null,
  setYieldStrength: () => {},
  tensileStrength: null,
  setTensileStrength: () => {},
  designTemp: null,
  setDesignTemp: () => {},
  designPressure: null,
  setDesignPressure: () => {},
  selectedMawpCalcThickness: null,
  setSelectedMawpCalcThickness: () => {},
  calcType: null,
  setCalcType: () => {},
  indicationItem: undefined,
  ffsDataValues: null,
})

export interface FfsSharedValueProviderProps {
  children: ReactNode
  componentModel: GeckoComponentModel | undefined
  thicknessCdf: GeckoComponentDataFrame | undefined
  inspection: GeckoInspection | undefined
}

export const FfsSharedValueProvider: FC<FfsSharedValueProviderProps> = ({
  children,
  componentModel,
  thicknessCdf,
  inspection,
}) => {
  return (
    <FfsSharedValuesContext.Provider
      value={useCreateFfsSharedValues(
        componentModel,
        thicknessCdf,
        inspection
      )}>
      {children}
    </FfsSharedValuesContext.Provider>
  )
}
